import React, { useEffect, useState } from 'react';
import { api, useAuth } from './AdminApp';
import Modal from './Modal';
import Loader from '../Components/Loader/Loader';

export default function Users() {
  const { user: me } = useAuth();
  const [list, setList] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);

  // create modal state
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'user' });

  // password reset modal state
  const [resetting, setResetting] = useState(null); // { id, username }
  const [newPassword, setNewPassword] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const d = await api('/admin/users');
      setList(d.users || []);
      setError('');
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const createUser = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    try {
      const d = await api('/admin/users/create', {
        method: 'POST',
        body: JSON.stringify(newUser),
      });
      setNotice(d.message || 'User created.');
      setCreating(false);
      setNewUser({ username: '', password: '', role: 'user' });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    try {
      const d = await api('/admin/users/password', {
        method: 'POST',
        body: JSON.stringify({ id: resetting.id, password: newPassword }),
      });
      setNotice(d.message || 'Password updated.');
      setResetting(null);
      setNewPassword('');
    } catch (err) {
      setError(err.message);
    }
  };

  const changeRole = async (u, role) => {
    setError('');
    setNotice('');
    try {
      const d = await api('/admin/users/role', {
        method: 'POST',
        body: JSON.stringify({ id: u.id, role }),
      });
      setNotice(d.message || 'Role updated.');
      load();
    } catch (err) {
      setError(err.message);
      load();
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user "${u.username}"? They will no longer be able to log in.`)) return;
    setError('');
    setNotice('');
    try {
      const d = await api('/admin/users/delete', {
        method: 'POST',
        body: JSON.stringify({ id: u.id }),
      });
      setNotice(d.message || 'User deleted.');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const isAdmin = me && me.role === 'admin';

  return (
    <div>
      <div className="admin-page-head">
        <h1 className="admin-page-title">Users</h1>
        {isAdmin && (
          <button className="admin-btn admin-btn-primary" onClick={() => setCreating(true)}>
            + Add user
          </button>
        )}
      </div>
      {error && <div className="admin-alert admin-alert-error">{error}</div>}
      {notice && <div className="admin-alert admin-alert-success">{notice}</div>}

      {loading ? (
        <Loader inline status="Loading users" />
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Username</th>
              <th>Role</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((u) => {
              const isMe = me && u.id === me.id;
              const builtIn = u.username === 'admin';
              return (
                <tr key={u.id}>
                  <td>{u.id}</td>
                  <td>
                    <strong>{u.username}</strong>
                    {isMe && <span className="admin-muted"> (you)</span>}
                  </td>
                  <td>
                    <span className={`admin-role-pill ${u.role === 'admin' ? 'is-admin' : 'is-user'}`}>
                      {u.role === 'admin' ? 'Admin' : 'User'}
                    </span>
                  </td>
                  <td>{u.created_at ? String(u.created_at).slice(0, 10) : '—'}</td>
                  <td className="admin-actions">
                    {isAdmin && !isMe && (
                      <select
                        className="admin-role-select"
                        value={u.role}
                        onChange={(e) => changeRole(u, e.target.value)}
                        disabled={builtIn}
                        title={builtIn ? 'The built-in admin account cannot be changed.' : 'Change role'}
                      >
                        <option value="admin">Admin</option>
                        <option value="user">User</option>
                      </select>
                    )}
                    <button
                      className="admin-btn admin-btn-sm"
                      onClick={() => {
                        setResetting(u);
                        setNewPassword('');
                      }}
                    >
                      Reset password
                    </button>
                    {isAdmin && !builtIn && !isMe && (
                      <button
                        className="admin-btn admin-btn-sm admin-btn-danger"
                        onClick={() => remove(u)}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Create user */}
      {creating && (
        <Modal title="New admin user" onClose={() => setCreating(false)}>
          <form className="admin-form" onSubmit={createUser}>
            <label>
              Username *
              <input
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                placeholder="e.g. receptionist"
                required
                minLength={3}
                maxLength={50}
              />
            </label>
            <label>
              Password * (min 6 characters)
              <input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                required
                minLength={6}
                autoComplete="new-password"
              />
            </label>
            <label>
              Role *
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              >
                <option value="user">User — content & leads only</option>
                <option value="admin">Admin — full access incl. settings, users, logs</option>
              </select>
            </label>
            <div className="admin-form-actions">
              <button type="submit" className="admin-btn admin-btn-primary">
                Create user
              </button>
              <button type="button" className="admin-btn" onClick={() => setCreating(false)}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Reset password */}
      {resetting && (
        <Modal title={`Reset password — ${resetting.username}`} onClose={() => setResetting(null)}>
          <form className="admin-form" onSubmit={resetPassword}>
            <label>
              New password * (min 6 characters)
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
              />
            </label>
            <div className="admin-form-actions">
              <button type="submit" className="admin-btn admin-btn-primary">
                Update password
              </button>
              <button type="button" className="admin-btn" onClick={() => setResetting(null)}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
